import { vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import { Antibody, AoE, BloodCell, Collision, Dead, OnScreen, Ownership, Renderable, SoundSource, Transform } from "../../../shared/components";
import { Camera, CameraZoomLevel, MouseControls, RenderEffects, Resolution, Speeds } from "../../../shared/constants";
import { Player } from "../../../shared/player";
import { SmoothNoiseMachine } from "../../../shared/rng";
import { Sound } from "../../../shared/sound";
import { animate, PropertyAnimation } from "../../../shared/systems/animationSystem";
import { PointOfInterestSystem } from "./pointOfInterestSystem";
import { StateSystem } from "../../../shared/systems/stateSystem";
import { Callback, clamp, getSystem, metersToPixels, pixelsToMeters, screenToWorldPosition, worldToScreenPosition, ExecutesEvenIfPaused } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { RenderEngine } from "../rendering/renderEngine";
import { lerp } from "../../../shared/interpolation";

const cameraStep = metersToPixels(Camera.StepMeters);
const cameraStep2d = new vec2([cameraStep, cameraStep]);
const maxGridY = Camera.MaxRadiusGrid;
const maxGridX = Camera.MaxRadiusGrid;
const minGridY = -Camera.MaxRadiusGrid;
const minGridX = -Camera.MaxRadiusGrid;
const maxScreenShakeOffset = 100;
const maxScreenShakeAngle = 10;

export class CameraSystem extends System implements ExecutesEvenIfPaused {
	game: GameClient;
	engine: RenderEngine;
	cameraPosition = vec2.zero.copy();
	cameraScale = vec2.one.copy();
	/**MS per pixel */
	cameraSpeed = Speeds.CameraNormalSpeed;
	screenShakeOffset = vec2.zero.copy();
	screenShakeAngle = 0;
	controlsLocked = false;
	isApertureFocused = false;

	private stateSystem!: StateSystem;

	private autoCameraSpeed = Speeds.CameraSlowSpeed;

	private cameraHasMoved = false;
	private sceneRoot!: PIXI.Container;
	private cameraTargetPosition: vec2 = vec2.zero.copy();
	private zoomLevel = CameraZoomLevel.Normal;
	private gridX = 0;
	private gridY = 0;
	private scaleFactor = 1;

	private traumaFalloffRate = 1;
	private trauma = 0;
	private xShakeNoise = new SmoothNoiseMachine(11);
	private yShakeNoise = new SmoothNoiseMachine(13);
	private rotationShakeNoise = new SmoothNoiseMachine(17);
	private noiseSampleRate = 1 / 3;

	private currentPanDirection = vec2.zero.copy();

	private targetFocalDistance = Camera.StandardFocalDistance;

	private isMousePanning = false;
	private lastPanX = 0;
	private lastPanY = 0;

	private pointOfInterestSystem!: PointOfInterestSystem;

	private cameraAnims = new Array<PropertyAnimation>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.engine = this.game.renderEngine;
		this.pointOfInterestSystem = getSystem(this.world, PointOfInterestSystem);
	}

	init() {
		this.stateSystem = getSystem(this.world, StateSystem);
		this.sceneRoot = this.engine.sceneRoot;

		document.addEventListener('wheel', (event) => {
			if (this.game.gameTakesKeyEvents) event.preventDefault();
			//else return;

			const direction = Math.sign(event.deltaY);
			const zoomLevel = clamp(this.getNearestZoomLevel() + direction, CameraZoomLevel.Normal, CameraZoomLevel.Far);
			this.setZoomLevel(zoomLevel);
			this.game.updateUI();
		}, { passive: false });

		document.addEventListener("keydown", (event) => {
			if (event.key === 'w' || event.key === "W" || event.key === 'ArrowUp') {
				if (this.controlsLocked) return;
				this.currentPanDirection.add(new vec2([0, Camera.KeyPressStep]));
				this.currentPanDirection.normalize().scale(Camera.KeyPressStep);
				if (this.game.gameTakesKeyEvents) event.preventDefault();
			}
			if (event.key === 's' || event.key === 'S' || event.key === 'ArrowDown') {
				if (this.controlsLocked) return;
				this.currentPanDirection.add(new vec2([0, -Camera.KeyPressStep]));
				this.currentPanDirection.normalize().scale(Camera.KeyPressStep);
				if (this.game.gameTakesKeyEvents) event.preventDefault();
			}
			if (event.key === 'd' || event.key === "D" || event.key === 'ArrowRight') {
				if (this.controlsLocked) return;
				this.currentPanDirection.add(new vec2([-Camera.KeyPressStep, 0]));
				this.currentPanDirection.normalize().scale(Camera.KeyPressStep);
				if (this.game.gameTakesKeyEvents) event.preventDefault();
			}
			if (event.key === 'a' || event.key === "A" || event.key === 'ArrowLeft') {
				if (this.controlsLocked) return;
				this.currentPanDirection.add(new vec2([Camera.KeyPressStep, 0]));
				this.currentPanDirection.normalize().scale(Camera.KeyPressStep);
				if (this.game.gameTakesKeyEvents) event.preventDefault();
			}

		}, { passive: false });

		document.addEventListener("keyup", (event) => {
			if (event.key === 'w' || event.key === "W" || event.key === 'ArrowUp' ||
				event.key === 's' || event.key === 'S' || event.key === 'ArrowDown' ||
				event.key === 'd' || event.key === "D" || event.key === 'ArrowRight' ||
				event.key === 'a' || event.key === "A" || event.key === 'ArrowLeft'
			) {
				this.currentPanDirection.xy = [0, 0];
			}
		});

		//Listen for right click to pan
		document.addEventListener("contextmenu", (event) => {
			event.preventDefault();
			return false;
		});

		document.addEventListener("mousedown", (event) => {
			if (event.button === MouseControls.RightButton) {
				this.isMousePanning = true;
				this.lastPanX = event.clientX;
				this.lastPanY = event.clientY;
				this.currentPanDirection.xy = [0, 0];
				this.cameraAnims.forEach(anim => anim.stop());
				this.cameraAnims = [];
			}
		});
		document.addEventListener("mouseup", (event) => {
			if (event.button === MouseControls.RightButton) {
				this.isMousePanning = false;
				this.lastPanX = 0;
				this.lastPanY = 0;
				this.currentPanDirection.xy = [0, 0];
				if (this.cameraMoveNotifications !== null) {
					this.cameraMoveNotifications(true, false, this.getCameraPositionInWorldSpace());
				}
				this.cameraHasMoved = true;
			}
		});
		document.addEventListener("mousemove", (event) => {
			if (this.isMousePanning) {
				const diff = new vec2([
					event.clientX - this.lastPanX,
					event.clientY - this.lastPanY])
				this.cameraPosition.add(diff);
				this.syncGridFromCameraPosition();
				this.checkIfFocusedOnPlayerCells();

				this.lastPanX = event.clientX;
				this.lastPanY = event.clientY;
			}
		});

		this.cameraPosition = new vec2([this.sceneRoot.position.x, this.sceneRoot.position.y]);
		this.cameraScale = new vec2([this.sceneRoot.scale.x, this.sceneRoot.scale.y]);
	}

	cameraMoveNotifications: ((isPan: boolean, isZoom: boolean, center: vec2) => void) | null = null;

	setCameraMoveNotifications(cb: typeof this.cameraMoveNotifications) {
		this.cameraMoveNotifications = cb;
	}

	execute(delta: number, time: number) {
		this.executeEvenIfPaused(delta, time);
	}

	executeEvenIfPaused(delta: number, time: number) {
		let cameraMovingManually = this.isMousePanning;
		const player = this.game.getPlayer();
		if (!this.currentPanDirection.equals(vec2.zero)) {
			this.freeMove(this.currentPanDirection.x, this.currentPanDirection.y);
			this.animateToTargetPositionAndScale().then(() => { this.cameraHasMoved = true });
			cameraMovingManually = true;
			if (this.cameraMoveNotifications !== null) {
				this.cameraMoveNotifications(true, false, this.getCameraPositionInWorldSpace());
			}
		}
		else if (this.game.autoCameraActive) {
			const rectangleOfInterest = this.pointOfInterestSystem.calculateRectangleOfInterest();
			if (rectangleOfInterest) {
				const poiCenter = rectangleOfInterest.center;
				const padding = 1.3;
				const targetScale = clamp(
					Math.min(
						Resolution.Width / metersToPixels(rectangleOfInterest.width * padding),
						Resolution.Height / metersToPixels(rectangleOfInterest.height * padding),
					),
					Camera.ZoomScaleLevel3Multiplayer,
					Camera.ZoomScaleLevel1,
				);
				const targetPos = this.calcGridPos(poiCenter);
				const scaleDiff = Math.abs(this.scaleFactor - targetScale);
				const posDiff = vec2.distance(new vec2([this.gridX, this.gridY]), targetPos);
				if (scaleDiff > 0.02 || posDiff > 0.3) {
					this.gridX = targetPos.x; //TODO(reza): I think this is creating some bugs where the far mode camera can be offset..
					this.gridY = targetPos.y;
					this.scaleFactor = targetScale;
					this.animateToTargetPositionAndScale();
				}
			}
		}

		if (this.cameraHasMoved && player) {
			//If we're very close to the exact spawn position pull us towards it
			const pos = this.getPlayerGridPos(player);
			const here = new vec2([this.gridX, this.gridY]);
			const distance = vec2.distance(pos, here);
			if (distance < 0.5) {
				this.gridX = pos.x;
				this.gridY = pos.y;
				this.animateToTargetPositionAndScale();
				this.checkIfFocusedOnPlayerCells();
			}
		}
		this.cameraHasMoved = false;

		if (cameraMovingManually && this.game.autoCameraActive) this.game.toggleAutoCamera(false);


		//Mark objects as being on screen or not
		this.queries.sprites.results.forEach(entity => {
			const renderable = entity.getComponent(Renderable);
			const sprite = renderable.sprite;
			if (sprite) {
				const isOnScreen = this.checkAndUpdateOnScreenComponent(entity, sprite);
				sprite.renderable = isOnScreen; //set the renderable flag here cause we can't wait for render system next loop.
			}
		});

		this.queries.particles.results.forEach(entity => {
			const aoe = entity.getComponent(AoE);
			const transform = entity.getComponent(Transform);
			const screenPos = worldToScreenPosition(new vec2([transform.position.x, transform.position.y]));
			this.checkAndUpdateOnScreenComponent(entity, {
				x: screenPos.x,
				y: screenPos.y,
				width: aoe.currentRadius * 2,
				height: aoe.currentRadius * 2
			})
		});

		//Screen shake
		this.queries.deadBlood.added?.forEach(entity => {
			if (entity.getComponent(Ownership).playerId == this.game.playerID) {
				this.impact(0.6);
			}
		});

		this.queries.aoe.added?.forEach(entity => {
			if (entity.getComponent(SoundSource).sound == Sound.Explosion) {
				this.impact(0.8);
			}
		});

		this.queries.collision.results.forEach(entity => {
			const collision = entity.getComponent(Collision);
			if (
				//AoE makes noise on creation not impact
				entity.hasComponent(AoE) ||
				collision.collidedWith.every(hit => hit.entity.hasComponent(AoE)) ||
				//No impact for non-damaging collisions
				collision.collidedWith.every(hit => hit.canDamage === false)
			) {
				return;
			}
			this.impact(0.5);
		});

		const deltaSeconds = delta / 1000;
		const shake = Math.pow(this.trauma, 3);
		this.trauma = Math.max(0, this.trauma - deltaSeconds * this.traumaFalloffRate * 2);

		this.screenShakeOffset.xy = [
			maxScreenShakeOffset * shake * this.xShakeNoise.unitScalerStep(this.noiseSampleRate),
			maxScreenShakeOffset * shake * this.yShakeNoise.unitScalerStep(this.noiseSampleRate)
		];
		this.screenShakeAngle = maxScreenShakeAngle * shake * this.rotationShakeNoise.unitScalerStep(this.noiseSampleRate);

		//Throw camera out of focus in response to zoom
		const zoomDelta = this.cameraScale.x - this.sceneRoot.scale.x; //assume uniform scaling
		if (zoomDelta !== 0) {
			this.engine.depthOfField.focusDistance -= Math.abs(zoomDelta);
		}
		else {
			this.recenterFocalDistance();
		}

		const cameraPos = this.cameraPosition.copy().add(this.screenShakeOffset);
		this.sceneRoot.position.set(...cameraPos.xy);
		this.sceneRoot.angle = this.screenShakeAngle;
		this.sceneRoot.scale.set(...this.cameraScale.xy);


		this.engine.update(delta, time, cameraPos, this.cameraScale.x);
	}

	private recenterFocalDistance() {
		let currentFocalDistance = this.engine.depthOfField.focusDistance;
		const delta = Math.abs(currentFocalDistance - this.targetFocalDistance);
		const move = Math.min(delta, Camera.FocalDistanceRackSpeed);
		if (currentFocalDistance > this.targetFocalDistance) currentFocalDistance -= move;
		if (currentFocalDistance < this.targetFocalDistance) currentFocalDistance += move;
		this.engine.depthOfField.focusDistance = currentFocalDistance
	}

	freeMove(x: number, y: number) {
		this.gridX = clamp(this.gridX + x, minGridX, maxGridX);
		this.gridY = clamp(this.gridY + y, minGridY, maxGridY);
		this.checkIfFocusedOnPlayerCells();
	}

	setPositionToPlayerInstant(player: Player) {
		const pos = this.getPlayerGridPos(player);
		this.gridX = pos.x;
		this.gridY = pos.y;
		this.cameraTargetPosition.xy = [
			Resolution.Width * ((1 - this.scaleFactor) / 2),
			Resolution.Height * ((1 - this.scaleFactor) / 2)
		];
		this.cameraTargetPosition.add(this.getGridCoords());
		this.cameraPosition = this.cameraTargetPosition.copy();
		this.checkIfFocusedOnPlayerCells();
	}

	setPositionInstant(worldPosition: vec2) {
		const pos = this.calcGridPos(worldPosition);
		this.gridX = pos.x;
		this.gridY = pos.y;
		this.cameraTargetPosition.xy = [
			Resolution.Width * ((1 - this.scaleFactor) / 2),
			Resolution.Height * ((1 - this.scaleFactor) / 2)
		];
		this.cameraTargetPosition.add(this.getGridCoords());

		this.cameraPosition = this.cameraTargetPosition.copy();
		this.checkIfFocusedOnPlayerCells();
	}

	async focusOnPlayer(player: Player) {
		const pos = this.getPlayerGridPos(player);
		this.gridX = pos.x;
		this.gridY = pos.y;
		//this.animateToTargetPositionAndScale(); //done in set zoom level
		const wait = this.setZoomLevel(CameraZoomLevel.Normal);
		return wait
	}

	async focusOnPosition(worldPosition: vec2) {
		const pos = this.calcGridPos(worldPosition);
		this.gridX = pos.x;
		this.gridY = pos.y;
		await this.animateToTargetPositionAndScale();
		this.checkIfFocusedOnPlayerCells();
	}

	checkIfFocusedOnPlayerCells(gridPos = new vec2([this.gridX, this.gridY])) {
		const player = this.game.getPlayer();
		if (!player) {
			this.game.focusedOnCells = false;
		}
		else {
			const pos = this.getPlayerGridPos(player);
			const here = gridPos;
			const distance = vec2.distance(here, pos);
			const threshold = Camera.ShopActivationDistance / cameraStep;
			this.game.focusedOnCells = distance < threshold && this.scaleFactor >= Camera.ZoomScaleLevel1;
		}
	}

	setAperatureFocus(isFocused: boolean, focusedRadius: number = RenderEffects.VignetteApertureFocused, duration = 0.2) {
		if (isFocused === this.isApertureFocused &&
			(isFocused && focusedRadius === this.engine.vignette.getAperture())
		) return;

		const radius = isFocused ? metersToPixels(focusedRadius) : metersToPixels(RenderEffects.VignetteApertureFull);
		this.engine.vignette.animateAperture(radius, duration);
		this.isApertureFocused = isFocused;
	}

	lockToPlayerViews(lock: boolean) {
		if (lock) {
			this.setZoomLevel(CameraZoomLevel.Normal);
		}
		this.controlsLocked = lock;
	}

	getZoomLevel() {
		return this.zoomLevel;
	}

	/** TraumaLevel: 0 - 1 */
	impact(traumaLevel: number) {
		this.trauma = Math.max(this.trauma, traumaLevel);
	}

	getFinalCameraSpeed() {
		return this.game.autoCameraActive ? this.autoCameraSpeed : this.cameraSpeed;
	}

	private getNearestZoomLevel(): CameraZoomLevel {
		const levels = [
			{ level: CameraZoomLevel.Normal, scale: Camera.ZoomScaleLevel1 },
			{ level: CameraZoomLevel.Medium, scale: Camera.ZoomScaleLevel2 },
			{ level: CameraZoomLevel.Far, scale: Camera.ZoomScaleLevel3Multiplayer },
		];
		return levels.reduce((nearest, curr) =>
			Math.abs(curr.scale - this.scaleFactor) < Math.abs(nearest.scale - this.scaleFactor) ? curr : nearest
		).level;
	}

	private calcGridPos(worldPosition: vec2) {
		const position = worldToScreenPosition(worldPosition)
			.subtract(new vec2([Resolution.Width / 2, Resolution.Height / 2]));
		return position.scale(1 / cameraStep).negate();
	}


	private getPlayerGridPos(player: Player) {
		if (!player) return vec2.zero.copy();
		return this.calcGridPos(new vec2(player.spawnPoint));
	}


	private async animateToTargetPositionAndScale() {
		this.cameraTargetPosition.xy = [
			Resolution.Width * ((1 - this.scaleFactor) / 2),
			Resolution.Height * ((1 - this.scaleFactor) / 2)
		];
		this.cameraTargetPosition.add(this.getGridCoords());
		//this.cameraHasMoved = true;
		this.cameraAnims.forEach(anim => anim.stop());
		this.cameraAnims = [];
		animate(this.cameraPosition, 'x', this.cameraTargetPosition.x, cameraStep * this.getFinalCameraSpeed(), lerp, this.cameraAnims);
		animate(this.cameraPosition, 'y', this.cameraTargetPosition.y, cameraStep * this.getFinalCameraSpeed(), lerp, this.cameraAnims);
		animate(this.cameraScale, 'x', this.scaleFactor, cameraStep * this.getFinalCameraSpeed(), lerp, this.cameraAnims);
		animate(this.cameraScale, 'y', this.scaleFactor, cameraStep * this.getFinalCameraSpeed(), lerp, this.cameraAnims);
		await Promise.all(this.cameraAnims.map(anim => anim.done));
		//this.cameraHasMoved = true;
	}

	getCameraPositionInWorldSpace() {
		const t1 = new vec2([this.gridX, this.gridY]).multiply(cameraStep2d);
		const t2 = t1.copy().add(new vec2([Resolution.Width / 2, Resolution.Height / 2]));
		const t3 = screenToWorldPosition(t2);
		return t3.negate();
	}

	private getGridCoords() {
		const s = this.scaleFactor;
		const fade = s <= Camera.ZoomScaleLevel2
			? Math.max(0, (s - Camera.ZoomScaleLevel3Multiplayer) / (Camera.ZoomScaleLevel2 - Camera.ZoomScaleLevel3Multiplayer))
			: 1;
		return new vec2([this.gridX, this.gridY]).multiply(cameraStep2d).scale(s * fade);
	}

	private syncGridFromCameraPosition() {
		const s = this.scaleFactor;
		const fade = s <= Camera.ZoomScaleLevel2
			? Math.max(0, (s - Camera.ZoomScaleLevel3Multiplayer) / (Camera.ZoomScaleLevel2 - Camera.ZoomScaleLevel3Multiplayer))
			: 1;
		const divisor = cameraStep * s * fade;
		if (divisor <= 0) return;
		const offsetX = Resolution.Width * ((1 - s) / 2);
		const offsetY = Resolution.Height * ((1 - s) / 2);
		this.gridX = clamp((this.cameraPosition.x - offsetX) / divisor, minGridX, maxGridX);
		this.gridY = clamp((this.cameraPosition.y - offsetY) / divisor, minGridY, maxGridY);
	}

	async setZoomLevel(level: CameraZoomLevel) {
		if (this.controlsLocked) return;
		if (this.game.autoCameraActive) { this.game.toggleAutoCamera(false); }
		if (this.cameraMoveNotifications) {
			this.cameraMoveNotifications(false, true, this.getCameraPositionInWorldSpace());
		}

		this.zoomLevel = level;
		if (level === CameraZoomLevel.Normal) {
			this.scaleFactor = Camera.ZoomScaleLevel1;
		}
		if (level === CameraZoomLevel.Medium) {
			this.scaleFactor = Camera.ZoomScaleLevel2;
		}
		else if (level === CameraZoomLevel.Far) {
			this.scaleFactor = this.game.players.length === 1
				? Camera.ZoomScaleLevel3SinglePlayer
				: Camera.ZoomScaleLevel3Multiplayer;
		}

		this.checkIfFocusedOnPlayerCells();

		await this.animateToTargetPositionAndScale();
	}

	private isOnScreen(rect: { x: number, y: number, width: number, height: number }) {
		const cameraAdjX = (rect.x * this.cameraScale.x) + this.cameraPosition.x;
		const cameraAdjY = (rect.y * this.cameraScale.y) + this.cameraPosition.y;
		const screenWidth = Resolution.Width;
		const screenHeight = Resolution.Height;
		return (cameraAdjX + rect.width) > 0
			&& (cameraAdjY + rect.height) > 0
			&& cameraAdjX < screenWidth
			&& cameraAdjY < screenHeight;
	}

	private checkAndUpdateOnScreenComponent(entity: Entity, rect: { x: number, y: number, width: number, height: number }) {
		const isOnScreen = this.isOnScreen(rect);
		//Changing this while paused might cause 
		//pile up of events in other systems
		if (this.game.paused) return isOnScreen;
		if (isOnScreen) {
			if (!entity.hasComponent(OnScreen)) {
				entity.addComponent(OnScreen);
			}
		}
		else {
			if (entity.hasComponent(OnScreen)) {
				entity.removeComponent(OnScreen);
			}
		}
		return isOnScreen;
	}


}

CameraSystem.queries = {
	sprites: { components: [Renderable, Transform] },
	particles: { components: [AoE, Transform] },

	//Screen shake related
	deadBlood: { components: [Dead, BloodCell, Ownership], listen: { added: true } },
	aoe: { components: [AoE, SoundSource, OnScreen], listen: { added: true } },
	collision: { components: [Collision, Antibody, OnScreen] }
}
